import { useState, useContext, useEffect } from "react";
import "../bootstrap.min.css";
import "../style.css";
import "../responsive.css";
import "../custom.css";
import PortalContext from "../../Pages/PortalContext";
import { CirclePlus } from "lucide-react";

export default function Documents() {
  const { portal } = useContext(PortalContext);

  const settings = portal?.data?.settings?.documents;

  const visible = settings?.visible;
  const title = settings?.title;
  const summary = settings?.summary;
  const initialCount = settings?.initial || 0;
  const required = settings?.required;

  // Seed from portal or start fresh
  const seedFromPortal =
    Array.isArray(portal?.documents) && portal.documents.length > 0
      ? portal.documents.map((d, i) => ({
          id: d.id ?? i,
          name: d.name ?? "",
          base64: d.base64 ?? "",
        }))
      : Array.from({ length: initialCount }, (_, i) => ({
          id: i,
          name: "",
          base64: "",
        }));

  const [documents, setDocuments] = useState(seedFromPortal);

  const handleAdd = () => {
    setDocuments((prev) => [...prev, { id: Date.now(), name: "", base64: "" }]);
  };

  const handleRemove = (id) => {
    if (documents.length > initialCount) {
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    }
  };

  // Convert file -> Base64
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleFileChange = async (id, file) => {
    if (!file) return;
    try {
      const base64String = await toBase64(file);
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === id
            ? {
                ...doc,
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified,
                base64: base64String,
              }
            : doc
        )
      );
    } catch (err) {
      console.error("Error reading file:", err);
    }
  };

  // Persist to Portal context whenever the list changes
  useEffect(() => {
    // Keep only documents with Base64
    const normalized = documents
      .filter((d) => !!d.base64)
      .map((d) => ({
        id: d.id,
        name: d.name,
        size: d.size,
        type: d.type,
        lastModified: d.lastModified,
        base64: d.base64,
      }));

    portal.documents = normalized;
  }, [documents, portal]);

  if (!visible) return null;

  return (
    <section className="mt-4">
      <div className="documents-header mb-2">
        <div className="d-flex align-items-center">
          <h4 className="mb-0">{title}</h4>
          <CirclePlus
            onClick={handleAdd}
            className="w-5 h-5 ml-2"
            color="green"
          />
        </div>
        {summary && <p className="text-muted mt-2">{summary}</p>}
      </div>

      <div className="row gx-3 gy-3 gy-md-4 ">
        {documents.map((doc, index) => {
          const hasDelete = index >= initialCount;
          return (
            <div key={doc.id} className="col-12 col-sm-6 col-md-4 mb-3 mb-md-4">
              <div
                className="position-relative p-3 h-100"
                style={{
                  border: "1px solid #cbcfd4ff",
                  borderRadius: 8,
                  background: "#f8f9fa",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {hasDelete && (
                  <button
                    type="button"
                    onClick={() => handleRemove(doc.id)}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      background: "#f8f9fa",
                      border: "none",
                      fontSize: "15px",
                      cursor: "pointer",
                      borderTopRightRadius: "8px",
                      zIndex: 10,
                      textAlign: "end",
                      padding: "6px 10px",
                      // border: "1px solid #cbcfd4ff",
                      width: "40px",
                    }}
                  >
                    ❌
                  </button>
                )}

                <input
                  type="file"
                  accept="application/pdf,image/*"
                  required={required}
                  className="form-control"
                  onChange={(e) =>
                    handleFileChange(doc.id, e.target.files?.[0] ?? null)
                  }
                  style={{
                    background: "#f8f9fa",
                    border: "none",
                    width: hasDelete ? "calc(100% - 56px)" : "100%",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
